import { ICheckOutDTO } from "@components/payment/card-checkout/types";
import { IRaffleDefaultDTO } from "@hooks/dtos/raffles/IRaffleDefaultDTO";
import { config } from "config";

export interface IRaffleCheckoutSessionDTO {
  signature: string;
  raffle?: IRaffleDefaultDTO;
  raffleKey?: string;
  campaign?: string;
  selectedTickets: string[];
  checkout?: ICheckOutDTO;
}

interface IRaffleCheckoutStorageDTO {
  activeSignature?: string;
  sessions: Record<string, IRaffleCheckoutSessionDTO>;
}

const storageKey = `@${config.appAgent}:raffle-default-checkouts`;

function canUseSessionStorage() {
  return typeof window !== "undefined" && Boolean(window.sessionStorage);
}

function getNormalizedTickets(tickets?: string[]) {
  return [...(tickets || [])].map(String).sort((a, b) => a.localeCompare(b));
}

function getCheckoutStorage(): IRaffleCheckoutStorageDTO {
  if (!canUseSessionStorage()) return { sessions: {} };

  const rawStorage = sessionStorage.getItem(storageKey);
  if (!rawStorage) return { sessions: {} };

  try {
    const parsed = JSON.parse(rawStorage) as IRaffleCheckoutStorageDTO;

    if (!parsed?.sessions || typeof parsed.sessions !== "object") {
      sessionStorage.removeItem(storageKey);
      return { sessions: {} };
    }

    return parsed;
  } catch {
    sessionStorage.removeItem(storageKey);
    return { sessions: {} };
  }
}

function saveCheckoutStorage(storage: IRaffleCheckoutStorageDTO) {
  if (!canUseSessionStorage()) return;
  sessionStorage.setItem(storageKey, JSON.stringify(storage));
}

export function buildRaffleCheckoutSignature(
  raffleReference?: string,
  tickets?: string[],
) {
  const normalizedReference = String(raffleReference || "").trim();
  const normalizedTickets = getNormalizedTickets(tickets).join("|");

  if (!normalizedReference || !normalizedTickets) return undefined;

  return `${normalizedReference}::${normalizedTickets}`;
}

export function getRaffleCheckoutSession(signature?: string) {
  const storage = getCheckoutStorage();
  const targetSignature = signature || storage.activeSignature;

  if (!targetSignature) return undefined;

  return storage.sessions[targetSignature];
}

export function saveRaffleCheckoutSession(
  data: Omit<IRaffleCheckoutSessionDTO, "signature">,
) {
  const signature = buildRaffleCheckoutSignature(
    data.raffleKey || data.campaign,
    data.selectedTickets,
  );

  if (!signature) return;

  const storage = getCheckoutStorage();
  storage.activeSignature = signature;
  storage.sessions[signature] = {
    ...data,
    signature,
    selectedTickets: getNormalizedTickets(data.selectedTickets),
  };

  saveCheckoutStorage(storage);
}

export function clearRaffleCheckoutSession(signature?: string) {
  if (!canUseSessionStorage()) return;

  if (!signature) {
    sessionStorage.removeItem(storageKey);
    return;
  }

  const storage = getCheckoutStorage();
  delete storage.sessions[signature];

  if (storage.activeSignature === signature) {
    storage.activeSignature = undefined;
  }

  saveCheckoutStorage(storage);
}
